import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency: string): string {
  return `${getCurrencySymbol(currency)} ${amount.toFixed(2)}`;
}

export function formatCurrencyWithSymbol(currency: string, amount: number): string {
  return `${getCurrencySymbol(currency)} ${amount.toFixed(2)}`;
}

export function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', AUD: 'A$', CAD: 'C$',
    CHF: 'Fr', INR: '₹', MYR: 'RM', SGD: 'S$', NZD: 'NZ$', AED: 'د.إ',
    ARS: '$', AFN: '؋', ALL: 'L', AMD: '֏', ANG: 'ƒ', AOA: 'Kz', AWG: 'ƒ',
    AZN: '₼', BAM: 'KM', BBD: '$', BDT: '৳', BGN: 'лв', BHD: '.د.ب',
    BIF: 'Fr', BMD: '$', BND: '$', BOB: 'Bs.', BRL: 'R$', BSD: '$',
    BTN: 'Nu.', BWP: 'P', BYN: 'Br', BZD: '$', CDF: 'Fr', CLP: '$',
    COP: '$', CRC: '₡', CUP: '$', CVE: '$', CZK: 'Kč', DJF: 'Fr',
    DKK: 'kr', DOP: '$', DZD: 'د.ج', EGP: '£', ERN: 'Nfk', ETB: 'Br',
    FJD: '$', FKP: '£', FOK: 'kr', GEL: '₾', GGP: '£', GHS: '₵',
    GIP: '£', GMD: 'D', GNF: 'Fr', GTQ: 'Q', GYD: '$', HKD: 'HK$',
    HNL: 'L', HRK: 'kn', HTG: 'G', HUF: 'Ft', IDR: 'Rp', ILS: '₪',
    IMP: '£', IQD: 'ع.د', IRR: '﷼', ISK: 'kr', JEP: '£', JMD: '$',
    JOD: 'د.ا', KES: 'Sh', KGS: 'с', KHR: '៛', KID: '$', KMF: 'Fr',
    KRW: '₩', KWD: 'د.ك', KYD: '$', KZT: '₸', LAK: '₭', LBP: 'ل.ل',
    LKR: 'Rs', LRD: '$', LSL: 'L', LYD: 'ل.د', MAD: 'د.م.', MDL: 'L',
    MGA: 'Ar', MKD: 'ден', MMK: 'Ks', MNT: '₮', MOP: 'P', MRU: 'UM',
    MUR: '₨', MVR: 'ރ.', MWK: 'MK', MXN: '$', MZN: 'MT', NAD: '$',
    NGN: '₦', NIO: 'C$', NOK: 'kr', NPR: '₨', OMR: 'ر.ع.', PAB: 'B/.',
    PEN: 'S/.', PGK: 'K', PHP: '₱', PKR: '₨', PLN: 'zł', PYG: '₲',
    QAR: 'ر.ق', RON: 'lei', RSD: 'дин.', RUB: '₽', RWF: 'Fr', SAR: 'ر.س',
    SBD: '$', SCR: '₨', SDG: 'ج.س.', SEK: 'kr', SHP: '£', SLE: 'Le',
    SOS: 'Sh', SRD: '$', SSP: '£', STN: 'Db', SYP: '£', SZL: 'L',
    THB: '฿', TJS: 'ЅМ', TMT: 'm', TND: 'د.ت', TOP: 'T$', TRY: '₺',
    TTD: '$', TVD: '$', TWD: 'NT$', TZS: 'Sh', UAH: '₴', UGX: 'Sh',
    UYU: '$', UZS: "so'm", VES: 'Bs.', VND: '₫', VUV: 'Vt', WST: 'T',
    XAF: 'Fr', XCD: '$', XDR: 'SDR', XOF: 'Fr', XPF: 'Fr', YER: '﷼',
    ZAR: 'R', ZMW: 'ZK', ZWL: '$'
  };
  return symbols[currency] || currency;
}

export function generateInvoiceNumber(lastNumber: string): string {
  const match = lastNumber.match(/INV-(\d+)/);
  if (match) {
    const num = parseInt(match[1], 10) + 1;
    return `INV-${String(num).padStart(4, '0')}`;
  }
  return 'INV-0001';
}

export function generateQuotationNumber(lastNumber: string, prefix: 'QT' | 'EST'): string {
  const match = lastNumber.match(new RegExp(`${prefix}-(\\d+)`));
  if (match) {
    const num = parseInt(match[1], 10) + 1;
    return `${prefix}-${String(num).padStart(4, '0')}`;
  }
  return `${prefix}-0001`;
}

export function compressLogo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // If the file is already small (under 150KB), resolve with the original file data URL
    // to preserve 100% pixel-perfect quality without resizing artifacts.
    const MAX_ORIGINAL_SIZE = 150 * 1024;
    if (file.size <= MAX_ORIGINAL_SIZE) {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(e.target?.result as string);
          return;
        }

        // Use a 512px target height for high-density rendering (e.g. retina, print)
        const targetHeight = 512;
        const aspectRatio = img.width / img.height;
        const targetWidth = targetHeight * aspectRatio;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Enable high-quality image smoothing to prevent alias artifacts
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function compressBlogImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(e.target?.result as string);
          return;
        }

        // Target max width = 800px, maintain aspect ratio
        const maxWidth = 800;
        let targetWidth = img.width;
        let targetHeight = img.height;

        if (img.width > maxWidth) {
          targetWidth = maxWidth;
          targetHeight = (maxWidth / img.width) * img.height;
        }

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        resolve(canvas.toDataURL('image/jpeg', 0.8)); // 80% quality JPEG
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function getSafeBlogImageUrl(image: string | undefined | null): string {
  if (!image) return '';
  if (image.includes('photo-1586486855514-8c633cc6fa98') || 
      image.includes('photo-1554224155-6f9664f0ec37') || 
      image.includes('tax-calculations')) {
    return 'https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?auto=format&fit=crop&w=800&q=80';
  }
  if (image.startsWith('/blog/')) {
    const mapping: Record<string, string> = {
      '/blog/invoice-guide.jpg': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80',
      '/blog/freelancer-tips.jpg': 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
      '/blog/invoice-receipt.jpg': 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80',
      '/blog/business-practices.jpg': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80',
      '/blog/payment-terms.jpg': 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=800&q=80',
      '/blog/tax-calculations.jpg': 'https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?auto=format&fit=crop&w=800&q=80'
    };
    return mapping[image] || 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80';
  }
  return image;
}

